import { ApplicationVariablesDto } from 'prequalificationV2/api/ApplicationApi';
import { BorrowerEmploymentStatus } from 'enums/ApplicationsVariables';
import { BorrowerRole } from 'enums/BorrowerRole';
import { DEFAULT_EMPLOYMENT_VARIABLES } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.constants';
import {
  CurrentEmploymentVariable,
  PreviousEmploymentVariable,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';
import { getEmploymentVariableName } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import { getStreetAddress } from 'prequalificationV2/utils/stringUtils';

export const setDefaultEmploymentsValues = (
  variables: ApplicationVariablesDto,
  borrowerEmploymentsAmount: number,
  borrowerType: BorrowerRole,
): any => {
  let defaultValues = {};

  for (let index = 1; index <= borrowerEmploymentsAmount; index++) {
    // eslint-disable-next-line @typescript-eslint/no-loop-func
    DEFAULT_EMPLOYMENT_VARIABLES.forEach((variable) => {
      defaultValues = {
        ...defaultValues,
        [getEmploymentVariableName(variable, borrowerType, index)]:
          variables[getEmploymentVariableName(variable, borrowerType, index)] || '',
      };

      defaultValues = {
        ...defaultValues,
        [getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)]: getStreetAddress(
          variables[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)]?.street_number,
          variables[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)]?.street_name,
        ),
        [getEmploymentVariableName(CurrentEmploymentVariable.City, borrowerType, index)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)]?.city || '',
        [getEmploymentVariableName(CurrentEmploymentVariable.State, borrowerType, index)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)]
            ?.state_or_province || '',
        [getEmploymentVariableName(CurrentEmploymentVariable.Zip, borrowerType, index)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.Address, borrowerType, index)]
            ?.zip_or_postal_code || '',
        [getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType, index)]: getStreetAddress(
          variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType, index)]?.street_number,
          variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType, index)]?.street_name,
        ),

        [getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType, index)]:
          variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType, index)]?.city || '',
        [getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType, index)]:
          variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType, index)]
            ?.state_or_province || '',
        [getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType, index)]:
          variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType, index)]
            ?.zip_or_postal_code || '',
      };
    });
  }

  // Previous employment is a single record and is required independently of current employment.
  // Seed its default values regardless of the current employments amount so that saved values are
  // still displayed when there are no current employments (e.g. coborrower with only previous employment).
  defaultValues = {
    ...defaultValues,
    [getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.EmployerName, borrowerType)] || '',
    [getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.Position, borrowerType)] || '',
    [getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.EmployerPhone, borrowerType)] || '',
    [getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.HireDate, borrowerType)] || '',
    [getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]: getStreetAddress(
      variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]?.street_number,
      variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]?.street_name,
    ),
    [getEmploymentVariableName(PreviousEmploymentVariable.City, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]?.city || '',
    [getEmploymentVariableName(PreviousEmploymentVariable.State, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]?.state_or_province || '',
    [getEmploymentVariableName(PreviousEmploymentVariable.Zip, borrowerType)]:
      variables[getEmploymentVariableName(PreviousEmploymentVariable.Address, borrowerType)]?.zip_or_postal_code || '',
  };

  const employmentStatus =
    variables[getEmploymentVariableName(CurrentEmploymentVariable.EmploymentStatus, borrowerType)];

  switch (employmentStatus) {
    case BorrowerEmploymentStatus.ActiveMilitaryW2: {
      defaultValues = {
        ...defaultValues,
        [getEmploymentVariableName(CurrentEmploymentVariable.BranchOfService, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.BranchOfService, borrowerType)] || '',
        [getEmploymentVariableName(CurrentEmploymentVariable.MilitaryPaygrade, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.MilitaryPaygrade, borrowerType)] || '',
      };
      break;
    }
    case BorrowerEmploymentStatus.GovernmentW2: {
      defaultValues = {
        ...defaultValues,
        [getEmploymentVariableName(CurrentEmploymentVariable.GovernmentPaygrade, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.GovernmentPaygrade, borrowerType)] || '',
      };
      break;
    }
    case BorrowerEmploymentStatus.RetiredMilitary: {
      defaultValues = {
        ...defaultValues,
        [getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.RetiredBranchOfService, borrowerType)] || '',
        [getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.RetiredMilitaryPaygrade, borrowerType)] || '',
        [getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)] || '',
      };
      break;
    }
    case BorrowerEmploymentStatus.RetiredNonMilitary: {
      defaultValues = {
        ...defaultValues,
        [getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)]:
          variables[getEmploymentVariableName(CurrentEmploymentVariable.RetirementDate, borrowerType)] || '',
      };
      break;
    }
  }

  return defaultValues;
};
